import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getDocument, listDocuments } from '../api/documents';
import { useAsyncData } from '../hooks/useAsyncData';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ErrorState, EmptyState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { CopyButton } from '../components/CopyButton';
import { DocumentPayloadDialog } from '../components/DocumentPayloadDialog';
import { DOCUMENT_LIST_TYPE_OPTIONS, type DocumentDetails, type DocumentListQuery, type DocumentStatus, type DocumentSummary, type DocumentType } from '../types/api';
import { InboundMessagesPanel } from './InboundMessagesPage';
import { describeDocumentDelivery } from '../lib/documentDelivery';

const documentStatuses: DocumentStatus[] = [
  'CREATED',
  'VALID',
  'INVALID',
  'READY',
  'RESENT',
  'REBUILT',
  'PENDING',
];

type MessageView = 'generated' | 'inbound';

// Pre-applies filters carried in the incoming URL (e.g. from a cross-link on an exchange,
// execution, or AS2 message detail page — see src/lib/crossLinks.ts), read once on first
// render, mirroring the pattern already used by the AS2 message list pages.
function initialFiltersFromSearch(search: string): DocumentListQuery {
  const params = new URLSearchParams(search);
  return {
    page: 1,
    pageSize: 20,
    sortBy: (params.get('sortBy') ?? 'updatedAt') as NonNullable<DocumentListQuery['sortBy']>,
    sortOrder: (params.get('sortOrder') ?? 'desc') as 'asc' | 'desc',
    documentType: (params.get('documentType') ?? '') as DocumentType | '',
    partnerId: params.get('partnerId') ?? '',
    status: (params.get('status') ?? '') as DocumentStatus | '',
    createdFrom: params.get('createdFrom') ?? '',
    createdTo: params.get('createdTo') ?? '',
    updatedFrom: params.get('updatedFrom') ?? '',
    updatedTo: params.get('updatedTo') ?? '',
    correlationId: params.get('correlationId') ?? '',
    traceId: params.get('traceId') ?? '',
    referenceId: params.get('referenceId') ?? '',
    filename: params.get('filename') ?? '',
  };
}

export function DocumentsPage({ initialView = 'generated' }: { initialView?: MessageView }) {
  const { search } = useLocation();
  const navigate = useNavigate();
  const requestedView = new URLSearchParams(search).get('view');
  const [activeView, setActiveView] = useState<MessageView>(requestedView === 'inbound' ? 'inbound' : initialView);

  function selectView(view: MessageView) {
    setActiveView(view);
    const params = new URLSearchParams(search);
    if (view === 'inbound') params.set('view', 'inbound'); else params.delete('view');
    navigate({ search: `?${params.toString()}` }, { replace: true });
  }

  return (
    <div className="page-stack">
      <section className="panel">
        <div className="panel-header">
          <div>
            <h1>Messages</h1>
            <p>Review generated documents and inbound messages from one workspace.</p>
          </div>
        </div>
        <div className="segmented-control" aria-label="Message view">
          <button type="button" aria-pressed={activeView === 'generated'} className={activeView === 'generated' ? 'active' : ''} onClick={() => selectView('generated')}>Generated documents</button>
          <button type="button" aria-pressed={activeView === 'inbound'} className={activeView === 'inbound' ? 'active' : ''} onClick={() => selectView('inbound')}>Inbound messages</button>
        </div>
      </section>
      {activeView === 'generated' ? <GeneratedDocumentsPanel /> : <InboundMessagesPanel />}
    </div>
  );
}

function GeneratedDocumentsPanel() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { density, setDensity, visibleColumns, setVisibleColumns } = useColumnVisibility({
    technicalIds: false,
    partner: true,
    version: true,
    updated: true,
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(() => new URLSearchParams(search).has('referenceId'));
  const [cursorHistory, setCursorHistory] = useState<Array<string | undefined>>([]);
  const [payloadDocument, setPayloadDocument] = useState<DocumentSummary | null>(null);
  const [payloadDetails, setPayloadDetails] = useState<DocumentDetails | null>(null);
  const [payloadLoading, setPayloadLoading] = useState(false);
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const payloadRequestId = useRef(0);
  // KNOWN EXCEPTION (docs/adr/pagination-model.md): this endpoint layers optional cursor
  // paging (`cursor`/`data.nextCursor`) on top of the standard 1-indexed page/pageSize used
  // by every other Operate-domain list. Unifying it with plain offset paging would require a
  // backend contract change, which is out of scope for this cleanup pass — left as-is.
  const [filters, setFilters] = useState<DocumentListQuery>(() => initialFiltersFromSearch(search));

  const { data, isLoading, error, refresh } = useAsyncData(
    () => listDocuments(filters),
    [JSON.stringify(filters)],
    { debounceMs: 250 },
  );
  const filterSignature = JSON.stringify({ ...filters, page: undefined, cursor: undefined });
  const previousFilterSignature = useRef(filterSignature);
  useEffect(() => {
    if (previousFilterSignature.current !== filterSignature) {
      previousFilterSignature.current = filterSignature;
      setCursorHistory([]);
      setFilters((current) => ({ ...current, page: 1, cursor: undefined }));
    }
  }, [filterSignature]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const keys: Array<keyof DocumentListQuery> = ['documentType', 'partnerId', 'status', 'correlationId', 'traceId', 'referenceId', 'filename', 'createdFrom', 'createdTo', 'updatedFrom', 'updatedTo'];
    keys.forEach((key) => {
      const value = filters[key];
      if (typeof value === 'string' && value) params.set(key, value); else params.delete(key);
    });
    if (filters.sortBy && filters.sortBy !== 'updatedAt') params.set('sortBy', filters.sortBy); else params.delete('sortBy');
    if (filters.sortOrder && filters.sortOrder !== 'desc') params.set('sortOrder', filters.sortOrder); else params.delete('sortOrder');
    const nextSearch = params.toString();
    if (nextSearch !== new URLSearchParams(search).toString()) navigate({ search: `?${nextSearch}` }, { replace: true });
  }, [filters, navigate, search]);

  const activeFilterCount = ['documentType', 'partnerId', 'status', 'correlationId', 'traceId', 'referenceId', 'filename', 'createdFrom', 'createdTo', 'updatedFrom', 'updatedTo']
    .filter((key) => Boolean(filters[key as keyof DocumentListQuery])).length;

  async function loadPayload(document: DocumentSummary) {
    const requestId = ++payloadRequestId.current;
    setPayloadDocument(document);
    setPayloadDetails(null);
    setPayloadError(null);
    setPayloadLoading(true);
    try {
      const details = await getDocument(document.id);
      if (requestId === payloadRequestId.current) setPayloadDetails(details);
    } catch (caught) {
      if (requestId === payloadRequestId.current) {
        setPayloadError(caught instanceof Error ? caught.message : 'Unable to load the payload.');
      }
    } finally {
      if (requestId === payloadRequestId.current) setPayloadLoading(false);
    }
  }

  function closePayload() {
    payloadRequestId.current += 1;
    setPayloadDocument(null);
    setPayloadDetails(null);
    setPayloadError(null);
    setPayloadLoading(false);
  }

  return (
    <section className="panel">
      <div className="panel-header documents-panel-header">
        <div><h2>Documents</h2><p>Browse generated documents with backend pagination, sorting, and filter criteria.</p></div>
        {data ? <span className="result-count" aria-live="polite">{data.total} document{data.total === 1 ? '' : 's'}</span> : null}
      </div>
      <div className="toolbar toolbar-grid documents-filter-grid">
        <label className="filter-field">
          <span>Document type</span>
          <select
            value={filters.documentType ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, documentType: event.target.value as DocumentType | '' }))}
          >
            <option value="">All document types</option>
            {DOCUMENT_LIST_TYPE_OPTIONS.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </label>
        <label className="filter-field">
          <span>Partner</span>
          <input
            placeholder="Partner code or ID"
            value={filters.partnerId ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, partnerId: event.target.value }))}
          />
        </label>
        <label className="filter-field">
          <span>Status</span>
          <select
            value={filters.status ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, status: event.target.value as DocumentStatus | '' }))}
          >
            <option value="">All statuses</option>
            {documentStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
        </label>
        <label className="filter-field">
          <span>Filename</span>
          <input
            placeholder="Contains…"
            value={filters.filename ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, filename: event.target.value }))}
          />
        </label>
        {showAdvancedFilters ? <><label className="filter-field">
          <span>Correlation ID</span>
          <input
            value={filters.correlationId ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, correlationId: event.target.value }))}
          />
        </label>
        <label className="filter-field">
          <span>Trace ID</span>
          <input
            value={filters.traceId ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, traceId: event.target.value }))}
          />
        </label>
        <label className="filter-field">
          <span>Reference ID</span>
          <input
            value={filters.referenceId ?? ''}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, referenceId: event.target.value }))}
          />
        </label>
        <label className="filter-field">
          <span>Sort field</span>
          <select
            value={filters.sortBy ?? 'updatedAt'}
            onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value as NonNullable<DocumentListQuery['sortBy']> }))}
          >
            <option value="updatedAt">Updated</option>
            <option value="createdAt">Created</option>
            <option value="documentType">Document type</option>
            <option value="status">Status</option>
            <option value="partnerId">Partner</option>
          </select>
        </label>
        <label className="filter-field">
          <span>Sort order</span>
          <select
            value={filters.sortOrder ?? 'desc'}
            onChange={(event) => setFilters((current) => ({ ...current, sortOrder: event.target.value as 'asc' | 'desc' }))}
          >
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </label>
        <label className="filter-field">
          <span>Created from</span>
          <input
            type="datetime-local"
            value={toDateTimeLocalValue(filters.createdFrom)}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, createdFrom: normalizeDateTimeLocal(event.target.value) }))}
          />
        </label>
        <label className="filter-field">
          <span>Created to</span>
          <input
            type="datetime-local"
            value={toDateTimeLocalValue(filters.createdTo)}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, createdTo: normalizeDateTimeLocal(event.target.value) }))}
          />
        </label>
        <label className="filter-field">
          <span>Updated from</span>
          <input
            type="datetime-local"
            value={toDateTimeLocalValue(filters.updatedFrom)}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, updatedFrom: normalizeDateTimeLocal(event.target.value) }))}
          />
        </label>
        <label className="filter-field">
          <span>Updated to</span>
          <input
            type="datetime-local"
            value={toDateTimeLocalValue(filters.updatedTo)}
            onChange={(event) => setFilters((current) => ({ ...current, page: 1, updatedTo: normalizeDateTimeLocal(event.target.value) }))}
          />
        </label>
        </> : null}
        <button type="button" className="ghost-button" aria-expanded={showAdvancedFilters} onClick={() => setShowAdvancedFilters((visible) => !visible)}>
          {showAdvancedFilters ? 'Fewer filters' : `More filters${activeFilterCount > 3 ? ` (${activeFilterCount - 3})` : ''}`}
        </button>
        <button type="button" onClick={() => void refresh()}>
          Refresh
        </button>
        <button
          type="button"
          className="ghost-button"
          disabled={activeFilterCount === 0 && filters.sortBy === 'updatedAt' && filters.sortOrder === 'desc'}
          onClick={() => {
            setShowAdvancedFilters(false);
            setFilters({
              page: 1,
              pageSize: 20,
              sortBy: 'updatedAt',
              sortOrder: 'desc',
              documentType: '',
              partnerId: '',
              status: '',
              createdFrom: '',
              createdTo: '',
              updatedFrom: '',
              updatedTo: '',
              correlationId: '',
              traceId: '',
              referenceId: '',
              filename: '',
            });
          }}
        >
          Reset
        </button>
      </div>
      <details className="table-display-options">
        <summary>Display options</summary>
      <div className="table-controls" aria-label="Document table display options">
        <div className="segmented-control" aria-label="Table density">
          <button type="button" aria-pressed={density === 'cozy'} className={density === 'cozy' ? 'active' : ''} onClick={() => setDensity('cozy')}>Cozy</button>
          <button type="button" aria-pressed={density === 'compact'} className={density === 'compact' ? 'active' : ''} onClick={() => setDensity('compact')}>Compact</button>
        </div>
        <label className="inline-check"><input type="checkbox" checked={visibleColumns.technicalIds} onChange={(event) => setVisibleColumns((current) => ({ ...current, technicalIds: event.target.checked }))} /> Technical IDs</label>
        <label className="inline-check"><input type="checkbox" checked={visibleColumns.partner} onChange={(event) => setVisibleColumns((current) => ({ ...current, partner: event.target.checked }))} /> Partner</label>
        <label className="inline-check"><input type="checkbox" checked={visibleColumns.version} onChange={(event) => setVisibleColumns((current) => ({ ...current, version: event.target.checked }))} /> Version</label>
        <label className="inline-check"><input type="checkbox" checked={visibleColumns.updated} onChange={(event) => setVisibleColumns((current) => ({ ...current, updated: event.target.checked }))} /> Updated</label>
      </div>
      </details>
      <div className="results-status" aria-live="polite">{isLoading ? 'Updating documents…' : data ? `Showing ${data.data.length} of ${data.total} documents on page ${filters.page ?? 1}.` : ''}</div>
      <div aria-busy={isLoading}>
      {isLoading ? <LoadingState label="Loading documents…" /> : null}
      {error ? <ErrorState message={error} onRetry={refresh} /> : null}
      {!isLoading && !error && data && data.data.length === 0 ? <EmptyState label="No documents found." /> : null}
      {!isLoading && !error && data && data.data.length > 0 ? (
        <>
          <div className="table-scroll documents-table-scroll" tabIndex={0} aria-label="Generated documents">
          <table className={`data-table documents-table data-table-${density}`}>
            <thead>
              <tr>
                <th>Document</th>
                <th>Type</th>
                {visibleColumns.partner ? <th>Partner</th> : null}
                <th>Status</th>
                <th>Delivery outcome</th>
                {visibleColumns.version ? <th>Version</th> : null}
                {visibleColumns.updated ? <th>Updated</th> : null}
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((document) => {
                const delivery = describeDocumentDelivery(document);
                return <tr key={document.id}>
                  <td data-label="Document">
                    <div className="table-primary">
                      <Link to={{ pathname: `/documents/${document.id}`, search }}>{document.filename || `${document.documentType} document`}</Link>
                      {visibleColumns.technicalIds ? (
                        <span className="inline-copy">
                          <span className="mono" title={document.id}>{shortId(document.id)}</span>
                          <CopyButton value={document.id} label="Copy document ID" />
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td data-label="Type">{document.documentType}</td>
                  {visibleColumns.partner ? <td data-label="Partner" title={document.partnerId ?? undefined}>{document.partnerId ? shortPartner(document.partnerId) : 'Not assigned'}</td> : null}
                  <td data-label="Status">
                    <StatusBadge value={document.status} />
                  </td>
                  <td data-label="Delivery outcome">
                    <div className="table-primary">
                      <StatusBadge value={delivery.label} />
                      <span>{delivery.detail}</span>
                      {visibleColumns.technicalIds && delivery.destination ? <span className="mono">{delivery.destination}</span> : null}
                    </div>
                  </td>
                  {visibleColumns.version ? <td data-label="Version">{document.currentVersionNumber}</td> : null}
                  {visibleColumns.updated ? <td data-label="Updated" title={formatRelativeTime(document.updatedAt)}>{new Date(document.updatedAt).toLocaleString('en-GB')}</td> : null}
                  <td data-label="Actions" className="actions-cell inbound-message-actions">
                    <button type="button" className="table-action-button" onClick={() => void loadPayload(document)}>View payload</button>
                    <Link className="table-action-link" to={{ pathname: `/documents/${document.id}`, search }}>View details</Link>
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
          </div>
          <div className="pagination-bar">
            <button
              type="button"
              onClick={() => {
                const previousCursor = cursorHistory[cursorHistory.length - 1];
                setCursorHistory((current) => current.slice(0, -1));
                setFilters((current) => ({ ...current, page: Math.max(1, (current.page ?? 1) - 1), cursor: previousCursor }));
              }}
              disabled={(filters.page ?? 1) <= 1}
            >
              Previous
            </button>
            <span>Page {filters.page ?? 1} · {data.total} total</span>
            <button
              type="button"
              onClick={() => {
                setCursorHistory((current) => [...current, filters.cursor]);
                setFilters((current) => ({ ...current, page: (current.page ?? 1) + 1, cursor: data.nextCursor }));
              }}
              disabled={!data.nextCursor}
            >
              Next
            </button>
          </div>
        </>
      ) : null}
      </div>
      <DocumentPayloadDialog
        document={payloadDocument}
        details={payloadDetails}
        isLoading={payloadLoading}
        error={payloadError}
        onClose={closePayload}
        onRetry={() => { if (payloadDocument) void loadPayload(payloadDocument); }}
      />
    </section>
  );
}

function shortId(value: string): string {
  const normalized = value.replace(/^doc_/, 'doc ');
  return normalized.length > 18 ? `${normalized.slice(0, 18)}...` : normalized;
}

function shortPartner(value: string): string {
  return value.length > 28 ? `${value.slice(0, 25)}…` : value;
}

function formatRelativeTime(value: string): string {
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1_000);
  const formatter = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second');
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
  return formatter.format(Math.round(hours / 24), 'day');
}

function normalizeDateTimeLocal(value: string): string {
  return value ? new Date(value).toISOString() : '';
}

function toDateTimeLocalValue(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

